import { useMemo } from 'react';
import { CommonChartProps, SeriesConfig, ChartType } from '../types';
import { mergeWithChartDefaults } from '../chart-config/defaults';
import { ChartColorPalette } from '../chart-config/colors';
import { getColorForKey } from '../utils';

/**
 * A custom React hook that normalizes and configures chart series data.
 *
 * @template TData - The type of data in each series item.
 * @template TChartProps - The type of chart props, extending an object with an optional data array.
 * @template TSeriesConfig - The type of configuration for each series.
 *
 * @param {CommonChartProps<TData, TChartProps, TSeriesConfig>['series']} series - The series data to be normalized.
 * @param {ChartType} chartType - The type of chart being rendered.
 * @param {Partial<TSeriesConfig>} [seriesProps] - Optional additional properties for each series.
 * @param {string[]} [colors] - Optional array of colors to use for the series.
 *
 * @returns {SeriesConfig<TData, TSeriesConfig>[]} An array of normalized series configurations.
 */
export function useNormalizedSeries<
  TData,
  TChartProps extends { data?: TData[] },
  TSeriesConfig,
>(
  series: CommonChartProps<TData, TChartProps, TSeriesConfig>['series'],
  chartType: ChartType,
  seriesProps?: Partial<TSeriesConfig>,
  colors?: string[],
): SeriesConfig<TData, TSeriesConfig>[] {
  return useMemo(() => {
    // Convert series to an array if it's not already
    const seriesArray = Array.isArray(series) ? series : [series];

    return seriesArray.map((item, index) => {
      // Determine the color for the series
      const color =
        colors && colors.length > 0
          ? colors[index % colors.length]
          : ChartColorPalette.default[index % ChartColorPalette.default.length];

      // Create the base configuration for the series
      const baseConfig =
        typeof item === 'string'
          ? { dataKey: item, color }
          : {
              ...item,
              color: item.color || color,
            };

      // Fallback to a generated color if none is provided
      baseConfig.color ||= getColorForKey(`${index}${item.dataKey}`);

      // Merge the base configuration with chart defaults and any additional props
      return mergeWithChartDefaults(chartType, {
        ...seriesProps,
        ...baseConfig,
      }) as SeriesConfig<TData, TSeriesConfig>;
    });
  }, [series, colors, chartType, seriesProps]);
}
